import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** Skips `notifyMutationError` — set on mutations whose component already renders its own error UI. */
      suppressErrorToast?: boolean
    }
  }
}

interface CreateQueryClientOptions {
  /** Called for every query error. Default: Sentry.captureException. */
  onQueryError?: (err: unknown) => void
  /** Called for every mutation error. Default: Sentry.captureException. */
  onMutationError?: (err: unknown) => void
  /** Surfaces mutation errors to the user (e.g. a toast). Skipped when a mutation sets `meta.suppressErrorToast`. */
  notifyMutationError?: (err: unknown) => void
}

/**
 * Returns a QueryClient that forwards every query and mutation error to Sentry,
 * tagged with category so they're distinguishable in the dashboard.
 */
export function createQueryClient(opts: CreateQueryClientOptions = {}): QueryClient {
  const onQueryError =
    opts.onQueryError ??
    ((err) => Sentry.captureException(err, { tags: { category: 'query' } }))
  const onMutationError =
    opts.onMutationError ??
    ((err) => Sentry.captureException(err, { tags: { category: 'mutation' } }))

  return new QueryClient({
    queryCache: new QueryCache({ onError: onQueryError }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        onMutationError(err)
        if (opts.notifyMutationError && mutation.meta?.suppressErrorToast !== true) {
          opts.notifyMutationError(err)
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: 1,
      },
    },
  })
}
